<template>
  <div ref="wrapper" class="content-wrapper">
    <v-card tile class="pa-2 ma-0 elevation-0 overflow-y-auto">
      <v-card-title class="py-2 px-4 subtitle-2">
        {{ currentMap }}
        <v-spacer />
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="green darken-1"
              small
              icon
              v-bind="attrs"
              v-on="on"
              @click.stop="openCreateLayerDialog"
            >
              <v-icon small>
                {{ mdiLayersPlus }}
              </v-icon>
            </v-btn>
          </template>
          <span>Add layer</span>
        </v-tooltip>
      </v-card-title>
      <v-divider />
      <template v-if="maps[currentMap] && maps[currentMap].children.length">
        <v-list dense rounded>
          <v-list-item-group
            v-model="active"
            color="primary"
          >
            <v-list-item
              v-for="item in maps[currentMap].children"
              :key="item.id"
              :value="item.id"
            >
              <template v-slot:default="{ active }">
                <v-list-item-content>
                  <v-list-item-title v-text="item.name"></v-list-item-title>
                </v-list-item-content>
                <v-list-item-action class="ma-0">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        color="red darken-1"
                        small
                        icon
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="openRemoveLayerDialog(item)"
                      >
                        <v-icon small>
                          {{ mdiLayersRemove }}
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>Remove layer</span>
                  </v-tooltip>
                </v-list-item-action>
              </template>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </template>
      <div
        v-else
        class="py-2 px-4 text-center subtitle-2"
      >
        No layers
      </div>
    </v-card>
    <Teleport to="#map">
      <create-runtime-layer-dialog
        v-if="createLayerDialogOpen"
        :item="newLayer"
        @close="closeCreateLayerDialog"
        @create="createLayer"
      />
    </Teleport>
    <Teleport to="#map">
      <remove-runtime-layer-dialog
        v-if="removeLayerDialogOpen"
        :item="removedLayer"
        @close="closeRemoveLayerDialog"
        @remove="removeLayer"
      />
    </Teleport>
    <Teleport to="#map">
      <simple-edit-marker-dialog
        v-if="simpleDialogOpen"
        :item="markerForEdit"
        :title="formInfo.title"
        :rows="formInfo.rows"
        @close="closeSimpleDialog"
      />
    </Teleport>
    <marker-context-menu
      v-if="showMarkerMenu"
      v-model="showMarkerMenu"
      :x="marker.x"
      :y="marker.y"
      :data="marker.data"
      :items="marker.items"
    />
  </div>
</template>

<script>
import { mdiLayersPlus, mdiLayersRemove } from '@mdi/js'
import Teleport from 'vue2-teleport';
import CustomMarkersControl from '../../plugins/leaflet-controls/custom-markers-control'
import CreateRuntimeLayerDialog from '../map-dialogs/CreateRuntimeLayerDialog.vue'
import RemoveRuntimeLayerDialog from '../map-dialogs/RemoveRuntimeLayerDialog.vue'
import SimpleEditMarkerDialog from '../map-dialogs/SimpleEditMarkerDialog.vue'
export default {
  props: {
    map: {
      default() { return null }
    }
  },
  components: {
    Teleport,
    CreateRuntimeLayerDialog,
    RemoveRuntimeLayerDialog,
    SimpleEditMarkerDialog,
    MarkerContextMenu: () => import('./markerContextmenu.vue')
  },
  data () {
    return {
      mdiLayersPlus,
      mdiLayersRemove,
      createLayerDialogOpen: false,
      removeLayerDialogOpen: false,
      simpleDialogOpen: false,
      newLayer: null,
      removedLayer: null,
      markerForEdit: null,
      active: null,
      maps: {},
      currentMap: this.map.options.currentMap,
      showMarkerMenu: false,
      marker: {
        x: 0,
        y: 0,
        data: null,
        items: []
      },
      formInfo: {}
    }
  },
  watch: {
    active() {
      this.updateActive()
    }
  },
  async mounted () {
    await this.fetchMaps()
    await this.changeMap()
    this.map.on('change-map-id', this.fetchMaps)
    this.map.on('change-map', this.changeMap)
  },
  beforeDestroy () {
    this.map.off('change-map-id', this.fetchMaps)
    this.map.off('change-map', this.changeMap)
  },
  methods: {
    async fetchMaps () {
      const { data } = await this.$axios.get(this.$store.getters.projectPath + '/configs/multi-map/' + this.map.options.currentMapId)
      .catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.maps = data.reduce((maps, row) => {
          if (maps.hasOwnProperty(row.name)) {
            maps[row.name][row.type] = row
          } else {
            maps[row.name] = { [row.type]: row, children: [] }
          }

          return maps
        }, {})
      }
    },
    async fetchLayers () {
      const { data } = await this.$axios.get(this.$store.getters.projectPath + '/runtime-layers/' + this.currentMap)
      .catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.maps[this.currentMap].children = data
      }
    },
    openCreateLayerDialog () {
      this.createLayerDialogOpen = true
      this.newLayer = { map_name: this.currentMap, category_id: null, color: '#FF0000', rotation: false }
    },
    openRemoveLayerDialog (item) {
      this.removeLayerDialogOpen = true
      this.removedLayer = { ...item }
    },
    closeCreateLayerDialog () {
      this.createLayerDialogOpen = false
      this.newLayer = null
    },
    closeRemoveLayerDialog () {
      this.removeLayerDialogOpen = false
      this.removedLayer = null
    },
    openSimpleDialog (marker, formInfo) {
      this.markerForEdit = marker
      this.formInfo = formInfo
      this.simpleDialogOpen = true
    },
    closeSimpleDialog () {
      this.simpleDialogOpen = false
      this.markerForEdit = null
      this.formInfo = {}
    },
    async changeMap () {
      if (this.active) {
        const control = this.map.customControls['markers_layer_' + this.active]
        if (control) {
          this.map.removeControl(control)
        }
      }

      this.currentMap = this.map.options.currentMap
      await this.fetchLayers()
    },
    createLayer (layer) {
      this.maps[this.currentMap].children.push(layer)
      this.active = layer.id
    },
    removeLayer (layer) {
      const control = this.map.customControls['markers_layer_' + layer.id]
      if (control) {
        this.map.removeControl(control)
      }

      const map = { ...this.maps[layer.map_name] }
      map.children = map.children.filter(l => l.id !== layer.id)

      this.$set(this.maps, layer.map_name, map)
      this.active = null
    },
    updateActive () {
      Object.keys(this.maps).forEach(map => {
        this.maps[map].children.forEach(layer => {
          const control = this.map.customControls['markers_layer_' + layer.id]
          if (control) {
            this.map.removeControl(control)
          }
        })
      })

      if (!this.active) return

      if (this.map.customControls.hasOwnProperty('ruler')) {
        this.map.removeControl(this.map.customControls.ruler)
      }

      if (this.map.customControls.hasOwnProperty('square')) {
        this.map.removeControl(this.map.customControls.square)
      }

      const layer = Object.keys(this.maps).reduce((layer, map) => {
        const foundLayer = this.maps[map].children.reduce((foundLayer, currentL) => {
          if (currentL.id === this.active) {
            return currentL
          } else {
            const control = this.map.customControls['markers_layer_' + currentL.id]
            if (control) {
              this.map.removeControl(control)
            }
            return foundLayer
          }
        }, null)
        return foundLayer || layer
      }, null)

      if (layer) {
        const control = new CustomMarkersControl({
          id: 'markers_layer_' + layer.id,
          layer: {
            ...layer,
            map_id: this.map.options.currentMapId
          },
          vue: this,
          defaultColor: layer.color,
          inactiveColor: 'grey'
        })

        this.map._container.focus()
        this.map.addControl(control)

        control.on('marker-contextmenu', this.showMarkerContextMenu.bind(this))
      }
    },
    showMarkerContextMenu (event) {
      this.marker = {
        x: event.x,
        y: event.y,
        data: event.data,
        items: event.items
      }

      this.showMarkerMenu = true
    },
    closeMarkerContextMenu () {
      this.showMarkerMenu = false
      this.marker = {
        x: 0,
        y: 0,
        data: null,
        items: []
      }
    }
  }
}
</script>